import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Users, Activity } from "lucide-react";
import { useRealtimeSubscription } from "@/hooks/use-realtime";
import { useMemo } from "react";

export function LiveVisitors() {
  const { data: liveVisitors } = useQuery({
    queryKey: ["live-visitors"],
    queryFn: async () => {
      const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
      
      const { data, error } = await supabase
        .from("website_analytics")
        .select("*")
        .gte("visited_at", thirtyMinutesAgo.toISOString());

      if (error) throw error;
      return data || [];
    },
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  // Real-time subscription for instant updates
  useRealtimeSubscription("website_analytics", ["live-visitors"]);

  const stats = useMemo(() => {
    if (!liveVisitors || liveVisitors.length === 0) {
      return {
        total: 0,
        unique: 0,
        pages: [],
        countries: [],
      };
    }

    const uniqueVisitors = new Set(liveVisitors.map(v => v.visitor_id)).size;
    const uniqueSessions = new Set(liveVisitors.map(v => v.session_id)).size;

    // Top pages in last 30 min
    const pageCount = liveVisitors.reduce((acc, curr) => {
      acc[curr.page_path] = (acc[curr.page_path] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topPages = Object.entries(pageCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([page, count]) => ({ page, count }));

    // Top countries
    const countryCount = liveVisitors.reduce((acc, curr) => {
      acc[curr.country] = (acc[curr.country] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topCountries = Object.entries(countryCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([country, count]) => ({ country, count }));

    return {
      total: uniqueSessions,
      unique: uniqueVisitors,
      pages: topPages,
      countries: topCountries,
    };
  }, [liveVisitors]);

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16" />
        <CardHeader className="relative">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-primary animate-pulse" />
                Live Visitors
              </CardTitle>
              <CardDescription>Active in the last 30 minutes</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="relative">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-4xl font-bold text-primary">{stats.total}</div>
              <p className="text-sm text-muted-foreground mt-1">Active Sessions</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-chart-2">{stats.unique}</div>
              <p className="text-sm text-muted-foreground mt-1">Unique Visitors</p>
            </div>
          </div>

          {stats.pages.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Active Pages
              </h4>
              <div className="space-y-2">
                {stats.pages.map((page, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground truncate max-w-[200px]">
                      {page.page}
                    </span>
                    <span className="font-medium bg-primary/10 px-2 py-0.5 rounded-full text-xs">
                      {page.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Live Traffic by Location</CardTitle>
          <CardDescription>Top countries in last 30 minutes</CardDescription>
        </CardHeader>
        <CardContent>
          {stats.countries.length > 0 ? (
            <div className="space-y-3">
              {stats.countries.map((country, index) => {
                const percentage = stats.total > 0 
                  ? Math.round((country.count / stats.total) * 100) 
                  : 0;
                return (
                  <div key={index} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{country.country}</span>
                      <span className="text-muted-foreground">
                        {country.count} ({percentage}%)
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              No active visitors right now
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
